import React from 'react';
import { Anime } from '../types/anime';

type EpisodeFields = Pick<Anime, 'watchedEp' | 'latestEp' | 'totalEp' | 'status'>;

/** How many episodes you've watched, how many are out, and how many the season has (0 = unknown) */
export function episodeCounts(anime: EpisodeFields) {
  const watched = Number(anime.watchedEp) || 0;
  const aired = Number(anime.latestEp) || 0;
  const savedTotal = Number(anime.totalEp) || 0;
  // More episodes out than the saved total (19/16): the total was a guess, so treat it as unknown
  const total = savedTotal > 0 && aired <= savedTotal ? savedTotal : 0;
  return {
    watched,
    aired,
    total,
    upcoming: anime.status === 'รอฉาย' || aired === 0,
    caughtUp: aired > 0 && watched >= aired,
    // Every episode is out (12/12), or the show is marked finished
    complete: aired > 0 && (anime.status === 'จบแล้ว' || aired === total),
  };
}

const summary = (c: ReturnType<typeof episodeCounts>) =>
  c.complete
    ? `ดูแล้ว ${c.watched} ตอน · ฉายจบครบ ${Math.max(c.total, c.aired)} ตอนแล้ว`
    : `ดูแล้ว ${c.watched} ตอน · ฉายแล้ว ${c.aired} ตอน${c.total ? ` · ทั้งหมด ${c.total} ตอน` : ''}`;

/**
 * Card version: "ดูแล้ว 1 · ฉายแล้ว 3/26" (or "ฉายจบ 12 ตอน" once all are out) over a bar where the
 * solid part is what you've watched, the amber part is out but not watched yet, and the rest
 * hasn't aired.
 */
export const EpisodeProgress: React.FC<{ anime: EpisodeFields }> = ({ anime }) => {
  const c = episodeCounts(anime);
  const scale = Math.max(c.total, c.aired, c.watched, 1);
  const pct = (n: number) => `${Math.min(100, (n / scale) * 100)}%`;

  return (
    <div title={summary(c)} className="leading-none">
      <div className="flex items-baseline justify-between gap-1 text-[10px] sm:text-[11px] whitespace-nowrap">
        <span className="text-slate-400">
          ดู<span className="hidden sm:inline">แล้ว</span>{' '}
          <span className={`font-semibold tabular-nums ${c.caughtUp ? 'text-emerald-300' : 'text-white'}`}>
            {c.watched}
          </span>
          {c.caughtUp && <span className="text-emerald-400"> ✓</span>}
        </span>
        {c.upcoming ? (
          <span className="text-indigo-300 font-medium">รอฉาย</span>
        ) : c.complete ? (
          <span className="text-sky-300">
            <span className="hidden sm:inline">ฉาย</span>จบ{' '}
            <span className="font-semibold tabular-nums">{Math.max(c.total, c.aired)}</span> ตอน
          </span>
        ) : (
          <span className="text-slate-400">
            ฉาย<span className="hidden sm:inline">แล้ว</span>{' '}
            <span className="text-white font-semibold tabular-nums">{c.aired}</span>
            {c.total > 0 && <span className="text-slate-500 tabular-nums">/{c.total}</span>}
          </span>
        )}
      </div>
      <div className="relative h-1.5 mt-1.5 rounded-full bg-white/[0.07] overflow-hidden">
        <div className="absolute inset-y-0 left-0 rounded-full bg-amber-400/45" style={{ width: pct(c.aired) }} />
        <div
          className={`absolute inset-y-0 left-0 rounded-full ${
            c.caughtUp ? 'bg-emerald-400' : 'bg-gradient-to-r from-fuchsia-500 to-fuchsia-400'
          }`}
          style={{ width: pct(c.watched) }}
        />
      </div>
    </div>
  );
};

/** One-line version for list rows: "ดู 1 · ฉาย 3/26" / "ดู 1 · จบ 12 ตอน" */
export const EpisodeCounts: React.FC<{ anime: EpisodeFields }> = ({ anime }) => {
  const c = episodeCounts(anime);
  return (
    <span title={summary(c)} className="text-slate-400 whitespace-nowrap tabular-nums">
      ดู{' '}
      <span className={`font-semibold ${c.caughtUp ? 'text-emerald-300' : 'text-white'}`}>{c.watched}</span>
      <span className="text-slate-600"> · </span>
      {c.upcoming ? (
        <span className="text-indigo-300">รอฉาย</span>
      ) : c.complete ? (
        <span className="text-sky-300">
          จบ <span className="font-semibold">{Math.max(c.total, c.aired)}</span> ตอน
        </span>
      ) : (
        <>
          ฉาย <span className="text-white font-semibold">{c.aired}</span>
          {c.total > 0 && <span className="text-slate-500">/{c.total}</span>}
        </>
      )}
    </span>
  );
};
